<template>
    <div class="search-bar-wrapper" ref="searchBarRef">
        <div class="search-bar form-field">
            <span class="search-bar__icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960">
                    <path d="M784-120 532-372q-30 24-69 38t-83 14q-109 0-184.5-75.5T120-580q0-109 75.5-184.5T380-840q109 0 184.5 75.5T640-580q0 44-14 83t-38 69l252 252-56 56ZM380-400q75 0 127.5-52.5T560-580q0-75-52.5-127.5T380-760q-75 0-127.5 52.5T200-580q0 75 52.5 127.5T380-400Z" />
                </svg>
            </span>
            <label class="visually-hidden" for="search">Search for products</label>
            <input 
                type="text"
                v-model="search"
                @input="onChange"
                @keydown.esc="closeSearch"
                @focus="openSearch"
                autocomplete="off"
                class="search-bar__input"
                placeholder="Search for a product..."
            />
            <transition name="fade">
                <button v-if="isOpen" class="search-bar__close-button" @click="closeSearch">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960">
                        <path d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" />
                    </svg>
                </button>
            </transition>
        </div>

        <transition name="fade">
            <div class="search-overlay" v-show="isOpen" @mousedown.prevent :style="overlayStyles">
                <div class="search-overlay__inner">
                    <div class="container p-0">
                        <div class="search-overlay__content">
                            <div class="row justify-content-center">
                                <div class="col-12 col-lg-10">
                                    <div class="product-search-list">
                                        <div class="product-search-list__header">
                                            <span class="title title--4">{{ hasSearched ? productSearchTitle : "Customer Favourites" }}</span>
                                            <span v-if="hasSearched" class="p">{{ products.length }} results found</span>
                                        </div>
                                        <div class="product-search-list__main">
                                            <template v-if="isLoading">
                                                <div class="loader-wrapper">
                                                    <span class="loader">
                                                    </span>
                                                </div>
                                            </template>
                                            <template v-else>
                                                <!-- Show product cards if search results exist -->
                                                <div v-if="products.length > 0" class="product-card-list product-card-list--search">
                                                    <a v-for="(product) in products" 
                                                    :key="product.id" 
                                                    :href="product.url"
                                                    title="Click to view product"
                                                    class="product-card">
                                                        <div class="product-card__image">
                                                            <img v-if="product.imageUrl" class="lazy-load" alt="" :src="product.imageUrl">
                                                        </div>
                                                        <p class="product-card__title p text-wrap-pretty">
                                                            {{ product.name }}
                                                        </p>
                                                    </a>
                                                </div>

                                                <!-- If no results found, show initial items underneath -->
                                                <div v-if="products.length === 0 && initialProducts.length > 0"> 
                                                    <div class="richtext mb-4" v-html="noResultsText"></div>
                                                    <p class="title title--5 mb-2">Check out our customer favourites</p>
                                                    <div class="product-card-list product-card-list--search">
                                                        <a v-for="(product) in initialProducts" 
                                                        :key="product.id" 
                                                        :href="product.url"
                                                        title="Click to view product"
                                                        class="product-card">
                                                            <div class="product-card__image">
                                                                <img v-if="product.imageUrl" class="lazy-load" alt="" :src="product.imageUrl">
                                                            </div>
                                                            <p class="product-card__title p text-wrap-pretty">
                                                                {{ product.name }}
                                                            </p>
                                                        </a>
                                                    </div>
                                                </div>
                                            </template>
                                        </div>
                                    </div>
                                </div>
                                
                                <div class="col-12 col-lg-10">
                                    <div class="product-search-list__footer">
                                        <div class="richtext text-center">
                                            <p>Can't find what you are looking for? <a href="/shop/">Browse all products</a></p>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-12 col-lg-10">
                                    <div class="product-search-list">
                                        <div class="product-search-list__header">
                                            <span class="title title--4">{{ hasSearched ? pagesSearchTitle : "Popular Pages" }}</span>
                                            <span v-if="hasSearched" class="p">{{ pages.length }} results found</span>
                                        </div>
                                        <div class="product-search-list__main">

                                            <template v-if="isLoading">
                                                <div class="loader-wrapper">
                                                    <span class="loader" >
                                                    </span>
                                                </div>
                                            </template>

                                            <template v-else>

                                                <template v-if="pages.length > 0">
                                                    <nav class="nav nav--vertical">
                                                        <ul>
                                                            <li v-for="(page) in pages" :key="page.id" class="nav__item">
                                                                <a class="nav__link" :href="page.url">
                                                                    {{ page.name }}
                                                                </a>
                                                            </li>
                                                        </ul>
                                                    </nav>
                                                </template>

                                                <!-- If no results found, show initial pages underneath -->
                                                <template v-if="pages.length === 0 && initialPages.length > 0">
                                                    <div class="richtext mb-4" v-html="noResultsText"></div>
                                                    <p class="title title--5 mb-2">Check out our popular links</p>
                                                    <nav class="nav nav--vertical">
                                                        <ul>
                                                            <li v-for="(page) in initialPages" :key="page.id" class="nav__item">
                                                                <a class="nav__link" :href="page.url">
                                                                    {{ page.name }}
                                                                </a>
                                                            </li>
                                                        </ul>
                                                    </nav>
                                                </template>
                                            </template>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
const GetHttp = () => import(/* webpackChunkName: 'http' */ '../../_http').then(v => v.default);

export default {
    name: 'SearchBar',
    props: {
        productSearchTitle: {
            type: String,
            default: 'Products'
        },
        pagesSearchTitle: {
            type: String,
            default: 'Other Pages'
        },
        noResultsText: {
            type: String,
            default: '<p>Nothing matched your search query, please try again</p>'
        },
        initialSearchResults: {
            type: Object,
            default: () => ({ products: [], pages: [] })
        },
        rootId: {
            type: Number,
            required: true
        }
    },
    setup(props) {
        const isLoading = ref(false);
        const searchBarRef = ref(null);
        const search = ref('');
        const isOpen = ref(false);
        const initialProducts = ref(props.initialSearchResults.products || []);
        const initialPages = ref(props.initialSearchResults.pages || []);
        const products = ref([]);
        const pages = ref([]);
        const drawerPadding = ref('0px');
        let timeoutId = null;
        const hasSearched = ref(false);

        const fetchResults = async () => {
            if (!search.value.trim()) {
                products.value = [...initialProducts.value];
                pages.value = [...initialPages.value];
                return;
            }

            isLoading.value = true;

            try {
                const http = await GetHttp();
                const { data } = await http.get(`/search/quicksearch?rootId=${props.rootId}&query=${encodeURIComponent(search.value)}`);
                products.value = data.products || [];
                pages.value = data.pages || [];
            } catch (error) {
                console.error('Search API error:', error);
                products.value = [];
                pages.value = [];
            } finally {
                setTimeout(() => {
                    isLoading.value = false;
                }, 300);
            }
        };

        // Watch input changes and debounce API calls
        watch(search, () => {
            clearTimeout(timeoutId);
            hasSearched.value = search.value.trim() !== '';
            if (!search.value.trim()) {
                products.value = [...initialProducts.value];
                pages.value = [...initialPages.value];
            } else {
                timeoutId = setTimeout(fetchResults, 300);
            }
        });

        const openSearch = async () => {
            products.value = [...initialProducts.value];
            pages.value = [...initialPages.value];
            isOpen.value = true;
            await nextTick();
            getDrawerPadding();
        };

        const closeSearch = () => {
            isOpen.value = false;
            hasSearched.value = false;

            setTimeout(() => {
                search.value = '';
                products.value = [...initialProducts.value];
                pages.value = [...initialPages.value];
                
                const inputElement = document.querySelector('.search-bar__input');
                if (inputElement) {
                    inputElement.blur();
                }
            }, 300);
        };

        const overlayStyles = computed(() => ({
            '--searchBarOffSet': drawerPadding.value
        }));

        const getDrawerPadding = () => {
            if (searchBarRef.value) {
                const rect = searchBarRef.value.getBoundingClientRect();
                drawerPadding.value = `${rect.bottom + 20}px`;
            }
        };

        const handleClickOutside = (event) => {
            setTimeout(() => {
                const searchBarInput = document.querySelector('.search-bar');
                const searchOverlayContent = document.querySelector('.search-overlay__content');

                if (
                    searchBarInput &&
                    !searchBarInput.contains(event.target) &&
                    searchOverlayContent &&
                    !searchOverlayContent.contains(event.target)
                ) {
                    closeSearch();
                    const inputElement = document.querySelector('.search-bar__input');
                    if (inputElement) {
                        inputElement.blur();
                    }
                }
            }, 50);
        };

        onMounted(() => {
            window.addEventListener('resize', getDrawerPadding);
            document.addEventListener('mousedown', handleClickOutside);
        });

        onBeforeUnmount(() => {
            window.removeEventListener('resize', getDrawerPadding);
            document.removeEventListener('mousedown', handleClickOutside);
        });

        return {
            search,
            isOpen,
            products,
            pages,
            isLoading,
            closeSearch,
            openSearch,
            searchBarRef,
            drawerPadding,
            overlayStyles,
            hasSearched,
            initialProducts,
            initialPages
        };
    }
};
</script>